import '../Styles/MEICPage.css';
import useStrategyDashboardStore from '../Stores/strategyDashboardStore';

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatSignedCurrency(value) {
  const number = toNumber(value);
  if (number === null) return '—';

  const formatted = Math.abs(number).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  if (number > 0) return `+${formatted}`;
  if (number < 0) return `-${formatted}`;
  return formatted;
}

function formatPercent(value, signed = false) {
  const number = toNumber(value);
  if (number === null) return '—';

  const percent = number * 100;
  const sign = signed && percent > 0 ? '+' : '';

  return `${sign}${percent.toFixed(1)}%`;
}

function formatDate(value) {
  if (!value) return '—';

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function getValueClass(value) {
  const number = toNumber(value);

  if (number === null || number === 0) return 'meicNeutral';
  return number > 0 ? 'meicPositive' : 'meicNegative';
}

function getWinRateClass(value) {
  const number = toNumber(value);

  if (number === null) return 'meicNeutral';
  if (number >= 0.67) return 'meicPositive';
  if (number <= 0.5) return 'meicNegative';

  return 'meicNeutral';
}

function getDailyStats(history) {
  const rows = Array.isArray(history) ? history.slice(-25) : [];

  const pnlValues = rows
    .map(row => toNumber(row.daily_pnl))
    .filter(value => value !== null);

  const pcrValues = rows
    .map(row => toNumber(row.premium_capture_rate))
    .filter(value => value !== null);

  const winRateValues = rows
    .map(row => toNumber(row.win_rate))
    .filter(value => value !== null);

  const positiveDays = pnlValues.filter(value => value > 0).length;
  const negativeDays = pnlValues.filter(value => value < 0).length;

  const runningPnl = pnlValues.reduce((sum, value) => sum + value, 0);

  const avgDailyPnl =
    pnlValues.length > 0 ? runningPnl / pnlValues.length : null;

  const avgPcr =
    pcrValues.length > 0
      ? pcrValues.reduce((sum, value) => sum + value, 0) / pcrValues.length
      : null;

  const avgWinRate =
    winRateValues.length > 0
      ? winRateValues.reduce((sum, value) => sum + value, 0) / winRateValues.length
      : null;

  return {
    runningPnl,
    avgDailyPnl,
    avgPcr,
    avgWinRate,
    positiveDays,
    negativeDays,
    rowsCounted: pnlValues.length,
  };
}

function MeicRecentResultsTable({ data }) {
  const rows = Array.isArray(data) ? data.slice(-10).reverse() : [];

  if (rows.length === 0) return null;

  return (
    <section className="meicTablePanel meicRecentSessionsPanel">
      <div className="meicPanelHeader meicPanelHeaderCompact">
        <div>
          <span className="meicSectionEyebrow">Daily Results</span>
          <h2>Recent MEIC Sessions</h2>
          <p>
            MEIC performance is summarized by day because the strategy can open
            multiple intraday iron condors during a single session.
          </p>
        </div>
      </div>

      <div className="meicTableWrap">
        <table className="meicHistoryTable meicHistoryTableCompact">
          <thead>
            <tr>
              <th>Date</th>
              <th>PnL</th>
              <th>Win Rate</th>
              <th>PCR</th>
            </tr>
          </thead>

          <tbody>
            {rows.map(row => (
              <tr key={`${row.date}-${row.daily_pnl}-${row.win_rate}`}>
                <td>{formatDate(row.date)}</td>

                <td className={getValueClass(row.daily_pnl)}>
                  {formatSignedCurrency(row.daily_pnl)}
                </td>

                <td className={getWinRateClass(row.win_rate)}>
                  {formatPercent(row.win_rate)}
                </td>

                <td className={getValueClass(row.premium_capture_rate)}>
                  {formatPercent(row.premium_capture_rate, true)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function MEICPage() {
  const meicDailyPerformanceHistory = useStrategyDashboardStore(
    state => state.meic.dailyEquityHistory
  );

  const latestMeicSession = useStrategyDashboardStore(
    state => state.meic.latestEquity
  );

  const meicError = useStrategyDashboardStore(
    state => state.meic.error
  );

  const history = Array.isArray(meicDailyPerformanceHistory)
    ? meicDailyPerformanceHistory
    : [];

  const latestRow = latestMeicSession || history[history.length - 1] || null;
  const dailyStats = getDailyStats(history);

  return (
    <section className="meicPage">
      <div className="meicHero">
        <div className="meicHeroContent">
          <span className="meicEyebrow">
            MEIC • 0-DTE • Multiple Entry • Income-Focused
          </span>

          <h1>MEIC</h1>
          <h2>Multiple Entry Iron Condor</h2>

          <p>
            MEIC is the full-scale 0-DTE premium-selling layer of the SPX Gamma
            Premium Sellers suite. It is built for traders who have developed enough
            capital to move beyond occasional participation and apply a systematic,
            repeatable intraday SPX iron condor process. The strategy uses regime
            context, dealer positioning, volatility structure, and real-time 0-DTE
            option flow to determine when premium-selling conditions are structurally
            justified.
          </p>

          <p>
            Inside the platform, ACCE and ARGOS serve as capital-growth engines, GEMIC
            provides the core daily 1-DTE expected-move structure, and MEIC represents
            the more capitalized income stage: larger deployment, multiple intraday
            entries, defined-risk iron condors, and regime-aware 0-DTE income
            generation.
          </p>

          <div className="meicHeroActions">
            <a
              className="meicPrimaryButton"
              href="https://spxgamma-regime-aware-meic-framework.spxgamma.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Full MEIC Framework
            </a>
          </div>
        </div>

        <div className="meicSummaryCard">
          <span className="meicSummaryLabel">Program Role</span>

          <div className="meicSummaryStatus">
            Capitalized 0-DTE Income Engine
          </div>

          <p>
            MEIC is designed for traders who have developed enough capital to
            deploy a structured intraday SPX premium-selling process using
            defined-risk iron condors.
          </p>

          <div className="meicSummaryGrid">
            <div className="meicSummaryMetric">
              <span className="meicSummaryLabel">Structure</span>
              <strong>Iron Condor</strong>
              <small>Defined-risk SPX spreads</small>
            </div>

            <div className="meicSummaryMetric">
              <span className="meicSummaryLabel">Timeframe</span>
              <strong>0-DTE</strong>
              <small>Intraday premium selling</small>
            </div>

            <div className="meicSummaryMetric">
              <span className="meicSummaryLabel">Deployment</span>
              <strong>Multi-Entry</strong>
              <small>Up to six condors per day</small>
            </div>

            <div className="meicSummaryMetric">
              <span className="meicSummaryLabel">Capital Model</span>
              <strong>~$30K</strong>
              <small>Full structure deployment</small>
            </div>
          </div>
        </div>
      </div>

      {meicError && (
        <div className="meicErrorState">
          <strong>MEIC data error</strong>
          <span>{meicError}</span>
        </div>
      )}

      {!meicError && history.length === 0 && (
        <div className="meicEmptyState">
          <strong>No MEIC daily performance data loaded yet.</strong>
          <span>
            Once the premium sellers snapshot includes MEIC daily performance
            rows, this page will display daily PnL, win rate, and premium capture.
          </span>
        </div>
      )}

      <div className="meicStatsGrid">
        <div className="meicStatCard">
          <span className="meicStatLabel">Last 25 Running PnL</span>
          <strong className={`meicStatValue ${getValueClass(dailyStats.runningPnl)}`}>
            {formatSignedCurrency(dailyStats.runningPnl)}
          </strong>
          <span className="meicStatSubtext">
            Profit/Loss from recent sessions
          </span>
        </div>

        <div className="meicStatCard">
          <span className="meicStatLabel">Latest Daily PnL</span>
          <strong className={`meicStatValue ${getValueClass(latestRow?.daily_pnl)}`}>
            {formatSignedCurrency(latestRow?.daily_pnl)}
          </strong>
          <span className="meicStatSubtext">Most recent daily result</span>
        </div>

        <div className="meicStatCard">
          <span className="meicStatLabel">Avg Win Rate</span>
          <strong className={`meicStatValue ${getWinRateClass(dailyStats.avgWinRate)}`}>
            {formatPercent(dailyStats.avgWinRate)}
          </strong>
          <span className="meicStatSubtext">
            Average across last {dailyStats.rowsCounted || 0} sessions
          </span>
        </div>

        <div className="meicStatCard">
          <span className="meicStatLabel">Premium Capture rate (PCR)</span>
          <strong className={`meicStatValue ${getValueClass(dailyStats.avgPcr)}`}>
            {formatPercent(dailyStats.avgPcr, true)}
          </strong>
          <span className="meicStatSubtext">
            PnL divided by premium collected
          </span>
        </div>
      </div>

      <div className="meicTwoColumnLayout">
        <div className="meicLeftColumn">
          <div className="meicInsightCard">
            <span className="meicSectionEyebrow">Strategy Objective</span>
            <h3>Structured 0-DTE Income Generation</h3>
            <p>
              MEIC is built to generate repeatable intraday premium collection through
              SPX iron condors. The objective is not aggressive account acceleration
              or directional prediction. The objective is to deploy capital when
              market structure, volatility behavior, and 0-DTE option flow support a
              defined-risk premium-selling environment.
            </p>
          </div>

          <div className="meicInsightCard">
            <span className="meicSectionEyebrow">Market Filters</span>
            <h3>Regime Aware Entry Selection</h3>
            <p>
              MEIC does not treat every 0-DTE session the same. Entry participation is
              filtered through regime context, dealer positioning, volatility pressure,
              and real-time option flow. This helps the strategy avoid forcing trades
              when the market environment does not support premium selling.
            </p>
          </div>

          <div className="meicInsightCard">
            <span className="meicSectionEyebrow">Capital Model</span>
            <h3>Designed For Larger Accounts</h3>
            <p>
              MEIC is the capitalized income layer of the SPX Gamma Premium Sellers
              suite. Full deployment is designed around roughly $30,000 of daily
              capital so multiple defined-risk condors can be placed while maintaining
              proper sizing discipline and avoiding over-concentration.
            </p>
          </div>
        </div>

        <div className="meicRightColumn">
          {!meicError && history.length > 0 && (
            <MeicRecentResultsTable data={history} />
          )}
        </div>
      </div>

      <section className="meicPanel">
        <div className="meicPanelHeader">
          <div>
            <span className="meicSectionEyebrow">Trade Structure</span>
            <h2>Full MEIC Deployment Model</h2>
            <p>
              A structured 0-DTE SPX iron condor framework with multiple
              potential entry windows and defined-risk construction.
            </p>
          </div>
        </div>

        <div className="meicStructureGrid">
          <div>
            <span>Instrument</span>
            <strong>SPX</strong>
          </div>

          <div>
            <span>Expiration</span>
            <strong>0-DTE</strong>
          </div>

          <div>
            <span>Structure</span>
            <strong>Iron Condor</strong>
          </div>

          <div>
            <span>Max Entries</span>
            <strong>6 / Day</strong>
          </div>

          <div>
            <span>Total Legs</span>
            <strong>12</strong>
          </div>

          <div>
            <span>Spread Width</span>
            <strong>50 Points</strong>
          </div>

          <div>
            <span>Min Credit</span>
            <strong>$1.80</strong>
          </div>

          <div>
            <span>Stop Framework</span>
            <strong>1× Credit</strong>
          </div>
        </div>
      </section>

      <div className="meicDisclaimer">
        MEIC trade posts and dashboard data are provided for educational and
        informational purposes only. They are not financial advice or a
        recommendation to buy or sell any security or options contract.
      </div>
    </section>
  );
}